import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { fetchRoleContexts } from '../services/authService';
import {
  CONTEXT_TYPES,
  DEFAULT_ROLE_CONTEXT,
  LANDING_ROUTES_BY_CONTEXT,
  ROLE_CONTEXT_LABELS,
  isKnownContextType,
} from '../constants/roleContext';
import { useAuth } from './AuthContext';

const RoleContext = createContext();
const ACTIVE_CONTEXT_KEY = 'activeContext';
const LOGOUT_EVENT = 'auth-logout';
const ROLE_CONTEXT_REFRESH_EVENT = 'auth-roles-updated';

const storage = {
  get: (key) => {
    if (typeof window === 'undefined') return null;
    try {
      const raw = localStorage.getItem(key) || sessionStorage.getItem(key);
      return normalizeContext(JSON.parse(raw || 'null'));
    } catch (_error) {
      return null;
    }
  },
  set: (key, value) => {
    if (typeof window === 'undefined') return;
    const serialized = JSON.stringify(value);
    localStorage.setItem(key, serialized);
    sessionStorage.setItem(key, serialized);
  },
  remove: (key) => {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  },
};

const normalizeContext = (value) => {
  if (!value || typeof value !== 'object') return null;
  const nextType = value.type || CONTEXT_TYPES.INDIVIDUAL;
  if (!isKnownContextType(nextType)) return null;
  return {
    type: nextType,
    entityType: value.entityType || null,
    entityId: value.entityId ? String(value.entityId) : null,
    entityName: value.entityName || null,
    label: value.label || ROLE_CONTEXT_LABELS[nextType] || value.label,
  };
};

const contextMatcher = (first, second) =>
  first?.type === second?.type
  && String(first?.entityType || '') === String(second?.entityType || '')
  && String(first?.entityId || '') === String(second?.entityId || '');

export const makeRoleKey = (role) => `${role?.type || ''}:${role?.entityType || ''}:${String(role?.entityId || '')}`;

export const rolesEqual = (first, second) => makeRoleKey(first) === makeRoleKey(second);


const getContextFromPathname = (pathname = '') => {
  const path = String(pathname || '').trim();
  const organizationMatch = path.match(/^\/dashboard\/organization\/([^/]+)\/(?:[^/]+)(?:\/|$)/i);
  if (organizationMatch?.[1]) {
    return {
      defaultType: CONTEXT_TYPES.ORGANIZATION_ADMIN,
      allowedTypes: [CONTEXT_TYPES.ORGANIZATION_ADMIN, CONTEXT_TYPES.DEPARTMENT_ADMIN],
      entityType: 'organization',
      entityId: organizationMatch[1],
    };
  }

  const communityMatch = path.match(/^\/dashboard\/community\/([^/]+)\/(?:[^/]+)(?:\/|$)/i);
  if (communityMatch?.[1]) {
    return {
      defaultType: CONTEXT_TYPES.COMMUNITY_ADMIN,
      allowedTypes: [CONTEXT_TYPES.COMMUNITY_ADMIN, CONTEXT_TYPES.DEPARTMENT_ADMIN],
      entityType: 'community',
      entityId: communityMatch[1],
    };
  }

  const clubMatch = path.match(/^\/dashboard\/club\/([^/]+)\/(?:[^/]+)(?:\/|$)/i);
  if (clubMatch?.[1]) {
    return {
      defaultType: CONTEXT_TYPES.CLUB_ADMIN,
      allowedTypes: [CONTEXT_TYPES.CLUB_ADMIN, CONTEXT_TYPES.DEPARTMENT_ADMIN],
      entityType: 'club',
      entityId: clubMatch[1],
    };
  }

  const tournamentAdminMatch = path.match(/^\/dashboard\/tournament-admin\/tournaments(?:\/|$)/i);
  if (tournamentAdminMatch) {
    return {
      defaultType: CONTEXT_TYPES.TOURNAMENT_ADMIN,
      allowedTypes: [CONTEXT_TYPES.TOURNAMENT_ADMIN],
      entityType: null,
      entityId: null,
    };
  }

  return null;
};

export const RoleProvider = ({ children }) => {
  const { token, user } = useAuth();
  const isAuthenticated = Boolean(token);
  const [availableContexts, setAvailableContexts] = useState([DEFAULT_ROLE_CONTEXT]);
  const [activeContext, setActiveContextState] = useState(DEFAULT_ROLE_CONTEXT);
  const [contextVersion, setContextVersion] = useState(0);
  const [isLoadingContexts, setIsLoadingContexts] = useState(false);
  const [rolesLoaded, setRolesLoaded] = useState(false);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const fetchKeyRef = useRef('');
  const persistedRoleKeyRef = useRef(makeRoleKey(DEFAULT_ROLE_CONTEXT));
  const individualPathGuardRef = useRef('');
  const location = useLocation();

  const getAvailableContexts = useCallback(() => availableContexts, [availableContexts]);

  const validateContext = useCallback((nextContext, contexts = []) => {
    const normalized = normalizeContext(nextContext);
    if (!normalized) return null;
    return contexts.find((ctx) => contextMatcher(ctx, normalized)) || null;
  }, []);

  const emitContextChange = useCallback((nextContext) => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('role-context-changed', { detail: nextContext }));
    }
    setContextVersion((version) => version + 1);
  }, []);

  const switchRole = useCallback(
    (nextContext) => {
      const normalized = normalizeContext(nextContext) || DEFAULT_ROLE_CONTEXT;
      setActiveContextState((previous) => {
        if (rolesEqual(previous, normalized)) return previous;
        emitContextChange(normalized);
        return normalized;
      });
      storage.set(ACTIVE_CONTEXT_KEY, normalized);
      return normalized;
    },
    [emitContextChange]
  );

  const setActiveContext = useCallback((nextContext) => switchRole(nextContext), [switchRole]);

  const resetToIndividual = useCallback(() => switchRole(DEFAULT_ROLE_CONTEXT), [switchRole]);

  const ensureIndividualContext = useCallback(() => {
    const pathname = location?.pathname || '';
    if (!pathname.startsWith('/dashboard/individual')) {
      individualPathGuardRef.current = '';
      return;
    }

    const isAlreadyIndividual = rolesEqual(activeContext, DEFAULT_ROLE_CONTEXT);
    const guardKey = `${pathname}:${makeRoleKey(activeContext)}`;
    if (isAlreadyIndividual || individualPathGuardRef.current === guardKey) {
      return;
    }

    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.debug('[redirect-debug] RoleContext.ensureIndividualContext -> resetting to individual context');
    }

    individualPathGuardRef.current = guardKey;
    switchRole(DEFAULT_ROLE_CONTEXT);
  }, [activeContext, location?.pathname, switchRole]);


  const activeContextKey = useMemo(() => makeRoleKey(activeContext), [activeContext]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (persistedRoleKeyRef.current === activeContextKey) return;

    persistedRoleKeyRef.current = activeContextKey;
    storage.set(ACTIVE_CONTEXT_KEY, activeContext);
  }, [activeContext, activeContextKey]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const stored = storage.get(ACTIVE_CONTEXT_KEY);
    if (stored && !rolesEqual(stored, DEFAULT_ROLE_CONTEXT)) {
      // Never auto-switch role context on mount; admin context should only be user-initiated.
      storage.remove(ACTIVE_CONTEXT_KEY);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      return;
    }

    fetchKeyRef.current = '';
    setRolesLoaded(false);
    setIsLoadingContexts(false);
    setAvailableContexts((previous) => {
      if (previous.length === 1 && rolesEqual(previous[0], DEFAULT_ROLE_CONTEXT)) return previous;
      return [DEFAULT_ROLE_CONTEXT];
    });
    setActiveContextState((previous) => (rolesEqual(previous, DEFAULT_ROLE_CONTEXT) ? previous : DEFAULT_ROLE_CONTEXT));
    persistedRoleKeyRef.current = makeRoleKey(DEFAULT_ROLE_CONTEXT);
    storage.remove(ACTIVE_CONTEXT_KEY);
  }, [isAuthenticated]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleRefresh = () => {
      fetchKeyRef.current = '';
      setRefreshNonce((value) => value + 1);
    };

    window.addEventListener(ROLE_CONTEXT_REFRESH_EVENT, handleRefresh);
    return () => window.removeEventListener(ROLE_CONTEXT_REFRESH_EVENT, handleRefresh);
  }, []);

  useEffect(() => {
    const userId = user?.id || user?._id;
    if (!isAuthenticated || !userId) return;

    const fetchKey = `${userId}:${token || ''}`;
    if (fetchKeyRef.current === fetchKey) return;
    fetchKeyRef.current = fetchKey;

    let mounted = true;
    setIsLoadingContexts(true);
    setRolesLoaded(false);

    fetchRoleContexts(token)
      .then((data) => {
        if (!mounted) return;
        const normalizedContexts = Array.isArray(data.contexts)
          ? data.contexts
            .map((ctx) => normalizeContext(ctx))
            .filter(Boolean)
          : [];

        const contexts = normalizedContexts.length
          ? normalizedContexts
          : [DEFAULT_ROLE_CONTEXT];

        if (!contexts.some((ctx) => ctx.type === CONTEXT_TYPES.INDIVIDUAL)) {
          contexts.unshift(DEFAULT_ROLE_CONTEXT);
        }

        setAvailableContexts((previous) => {
          const previousHash = previous.map(makeRoleKey).sort().join(',');
          const nextHash = contexts.map(makeRoleKey).sort().join(',');
          return previousHash === nextHash ? previous : contexts;
        });
      })
      .catch(() => {
        if (!mounted) return;
        setAvailableContexts((previous) => {
          if (previous.length === 1 && rolesEqual(previous[0], DEFAULT_ROLE_CONTEXT)) return previous;
          return [DEFAULT_ROLE_CONTEXT];
        });
      })
      .finally(() => {
        if (mounted) {
          setIsLoadingContexts(false);
          setRolesLoaded(true);
        }
      });

    return () => {
      mounted = false;
    };
  }, [isAuthenticated, refreshNonce, token, user?.id, user?._id]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleLogout = () => {
      persistedRoleKeyRef.current = makeRoleKey(DEFAULT_ROLE_CONTEXT);
      storage.remove(ACTIVE_CONTEXT_KEY);
      fetchKeyRef.current = '';
      setRolesLoaded(false);
      setAvailableContexts([DEFAULT_ROLE_CONTEXT]);
      setActiveContextState(DEFAULT_ROLE_CONTEXT);
    };

    window.addEventListener(LOGOUT_EVENT, handleLogout);
    return () => window.removeEventListener(LOGOUT_EVENT, handleLogout);
  }, []);

  useEffect(() => {
    ensureIndividualContext();
  }, [ensureIndividualContext]);

  useEffect(() => {
    const routeContext = getContextFromPathname(location?.pathname || '');
    if (!routeContext) return;

    const routeType = routeContext.allowedTypes?.includes(activeContext?.type)
      ? activeContext?.type
      : routeContext.defaultType;

    const nextContext = {
      type: routeType,
      entityType: routeContext.entityType || null,
      entityId: routeContext.entityId || null,
      entityName: activeContext?.entityId === routeContext.entityId
        && activeContext?.entityType === routeContext.entityType
        ? activeContext?.entityName || null
        : null,
      label: ROLE_CONTEXT_LABELS[routeType],
    };

    if (rolesEqual(activeContext, nextContext)) {
      return;
    }

    switchRole(nextContext);
  }, [activeContext, location?.pathname, switchRole]);

  const groupedContexts = useMemo(() => {
    const grouped = new Map();
    availableContexts.forEach((context) => {
      const existing = grouped.get(context.type) || {
        type: context.type,
        label: ROLE_CONTEXT_LABELS[context.type] || context.label,
        entities: [],
      };
      if (context.entityId) {
        existing.entities.push(context);
      }
      grouped.set(context.type, existing);
    });
    return Array.from(grouped.values());
  }, [availableContexts]);

  const getLandingPath = useCallback((context) => {
    const fallback = LANDING_ROUTES_BY_CONTEXT[context?.type] || '/dashboard/individual/home';
    const contextEntityId = context?.entityId ? String(context.entityId) : '';

    if (!contextEntityId) {
      return fallback;
    }

    if (context?.type === CONTEXT_TYPES.ORGANIZATION_ADMIN) {
      return `/dashboard/organization/${contextEntityId}/tournaments`;
    }

    if (context?.type === CONTEXT_TYPES.COMMUNITY_ADMIN) {
      return `/dashboard/community/${contextEntityId}/tournaments`;
    }

    if (context?.type === CONTEXT_TYPES.CLUB_ADMIN) {
      return `/dashboard/club/${contextEntityId}/tournaments`;
    }

    if (context?.type === CONTEXT_TYPES.TOURNAMENT_ADMIN) {
      return '/dashboard/tournament-admin/tournaments';
    }

    if (context?.type === CONTEXT_TYPES.DEPARTMENT_ADMIN) {
      if (context?.entityType === 'organization') {
        return `/dashboard/organization/${contextEntityId}/players/approvals`;
      }
      if (context?.entityType === 'community') {
        return `/dashboard/community/${contextEntityId}/players/approvals`;
      }
      if (context?.entityType === 'club') {
        return `/dashboard/club/${contextEntityId}/players/approvals`;
      }
      return '/team-admin';
    }

    return fallback;
  }, []);

  const value = useMemo(
    () => ({
      activeContext,
      availableContexts,
      contextVersion,
      groupedContexts,
      isLoadingContexts,
      rolesLoaded,
      switchRole,
      setActiveContext,
      resetToIndividual,
      ensureIndividualContext,
      getAvailableContexts,
      validateContext,
      getLandingPath,
    }),
    [
      activeContext,
      availableContexts,
      contextVersion,
      getAvailableContexts,
      getLandingPath,
      groupedContexts,
      isLoadingContexts,
      resetToIndividual,
      ensureIndividualContext,
      rolesLoaded,
      setActiveContext,
      switchRole,
      validateContext,
    ]
  );

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
};

export const useRoleContext = () => {
  const context = useContext(RoleContext);
  if (!context) {
    throw new Error('useRoleContext must be used within a RoleProvider');
  }
  return context;
};
